import CustomInput from "../CustomInput";
import CustomSelect from "../CustomSelect";
import DataTable, { Column } from "../DataTable";
import Tag from "../Tags";
import CustomButton from "../CustomButton";
import { InvoiceRow } from "../modal/CreateInvoice";

interface Props {
  rows: InvoiceRow[];
  updateValue: (
    index: number,
    field: keyof InvoiceRow,
    value: string
  ) => void;
  addRow: () => void;
  setRows: React.Dispatch<React.SetStateAction<InvoiceRow[]>>;
  calculateTotals: (rows: InvoiceRow[]) => void;
}

export default function InvoiceItemsTable({
  rows,
  updateValue,
  addRow,
  setRows,
  calculateTotals,
}: Props) {
  const columns: Column<InvoiceRow>[] = [
    { label: "SL NO.", accessor: "id" },

    {
      label: "Particular",
      accessor: "itemName",
      render: (row, index) => (
        <CustomInput
          name="itemName"
          placeholder="Enter item name"
          value={row.itemName}
          onChange={(e) => updateValue(index, "itemName", e.target.value)}
          inputClassName="w-32"
        />
      ),
    },

    {
      label: "Amount",
      accessor: "amount",
      render: (row, index) => (
        <CustomInput
          name="amount"
          placeholder="0.00"
          value={row.amount}
          onChange={(e) => updateValue(index, "amount", e.target.value)}
          inputClassName="w-24"
        />
      ),
    },

    {
      label: "Discount",
      accessor: "discount",
      render: (row, index) => (
        <div className="flex gap-2">
          <CustomInput
            name="discount"
            placeholder="0"
            value={row.discount}
            onChange={(e) => updateValue(index, "discount", e.target.value)}
            inputClassName="w-20"
          />
          <CustomSelect
            name="discountType"
            value={row.discountType}
            options={["₹", "%"]}
            onChange={(e) =>
              updateValue(index, "discountType", e.target.value)
            }
            selectClassName="w-16"
          />
        </div>
      ),
    },

    {
      label: "Discount Remark",
      accessor: "discountRemark",
      render: (row, index) => (
        <CustomInput
          name="discountRemark"
          placeholder="Remark"
          value={row.discountRemark}
          onChange={(e) =>
            updateValue(index, "discountRemark", e.target.value)
          }
          inputClassName="w-32"
        />
      ),
    },

    {
      label: "GST",
      accessor: "gst",
      render: (row, index) => (
        <CustomSelect
          name="gst"
          placeholder="GST %"
          value={row.gst}
          options={["0%", "5%", "10%", "12%", "18%", "28%"]}
          onChange={(e) => updateValue(index, "gst", e.target.value)}
          selectClassName="w-20"
        />
      ),
    },

    {
      label: "Item Total",
      accessor: "totalAmount",
      render: (row) => {
        const amount = parseFloat(row.amount || "0");
        const discount = parseFloat(row.discount || "0");
        const gstPercentage = parseFloat(row.gst.replace("%", "") || "0");

        let discountedAmount = amount;

        if (row.discountType === "%" && discount > 0) {
          discountedAmount = amount - (amount * discount) / 100;
        } else if (row.discountType === "₹" && discount > 0) {
          discountedAmount = amount - discount;
        }

        const gstAmount = discountedAmount * (gstPercentage / 100);
        const itemTotal = discountedAmount + gstAmount;

        return <p>₹{itemTotal.toFixed(2)}</p>;
      },
    },

    {
      label: "Action",
      accessor: "action",
      render: (_, index) => (
        <Tag
          text="Delete"
          color="danger"
          onClick={() => {
            const updated = rows.filter((_, i) => i !== index);
            setRows(updated);
            calculateTotals(updated);
          }}
        />
      ),
    },
  ];

  return (
    <>
      <DataTable columns={columns} data={rows} />
      <div className="flex justify-end items-end py-4 pb-7">
        <CustomButton
          text="+ Add New Item"
          color="bg-primary-500"
          onClick={addRow}
          type="button"
        />
      </div>
    </>
  );
}
